// 레이드 파티 직업 AI VFX(CHARACTER-PRD: 용병 = 직업 AI 캐릭터). 역할(직업)마다 모듈 하나: 공격(onAttack)·기술(onSkill)·매 프레임(update).
// 지금 코드의 역할은 tank(수호자 가렌)·archer(궁수 이브)·priest(사제 루멘). 마법사(mage)·검사(warrior) 직업 AI가 생기면 같은 표에서 고른다.
//   수호자: 근접 타격 불똥 / 도발 = 외침 고리(바닥·공중) + 방패 빛 + 발밑 도발 고리(도발 시간 동안 따라감) + 보스 머리 위 위협 글리프
//   궁수: 화살(흰 심 + 초록 꼬리 + 불티) → 명중 불똥·광휘
//   사제: 빛 구슬 공격 / 치유 = 대상 빛기둥 + 금빛 입자 + 대상 발밑 치유 원 + 사제 발밑 시전 원 + 사제→대상 금빛 줄기
//   마법사: 비전 화살(보라 구슬·줄) → 작은 폭발 고리 / 검사: 보스 몸에 엇갈린 참격 줄 두 개 + 불똥
// 모든 연출은 공용 풀(입자·바닥·표식·투사체)에 쓰므로 직업이 늘어도 드로우콜이 늘지 않는다.
import { AddKind, Orient, type RaidMarkers, type RaidParticles } from './raidVfxParticles';
import { GlowKind, type RaidGround } from './raidVfxGround';
import { ProjKind, type RaidProjectiles } from './raidVfxProjectiles';

export type PartyRole = 'tank' | 'archer' | 'priest' | 'mage' | 'warrior';
export const PARTY_ROLES: readonly PartyRole[] = ['tank', 'archer', 'priest', 'mage', 'warrior'];

/** 직업 색(빛 효과) */
export const ROLE_COLOR: Readonly<Record<PartyRole, number>> = {
  tank: 0x6aa8ff, archer: 0x9fe86a, priest: 0xffd77a, mage: 0xb47bff, warrior: 0xff8a4a,
};

/** 표식 칸(RaidMarkers): 파티 모듈이 쓰는 칸 범위. RaidVfx는 그 뒤 칸을 쓴다 */
export const PARTY_MARKER = { tankShield: 0, threatGlyph: 1, pillar0: 2, pillarN: 4, castGlow: 6 } as const;
export const PARTY_MARKER_END = 7;

/** 파티 연출이 읽는 장면 상태(RaidVfx가 매 프레임 채운다) */
export interface PartyCtx {
  readonly add: RaidParticles;
  readonly glow: RaidGround;
  readonly marks: RaidMarkers;
  readonly proj: RaidProjectiles;
  /** 보스(보간·부드럽게 따라간 몸) 위치·몸 반경·몸 가운데 높이·머리 위 높이 */
  bossX: number; bossZ: number; bossR: number; bossMidY: number; bossTopY: number;
  /** 파티 칸 위치(0 영웅, 1..3 용병) — 보간된 값 */
  readonly slotX: Float32Array; readonly slotZ: Float32Array;
  /** 세계 시간(초) */
  time: number;
  /** 게임 도발 남은 틱(수호자가 쓰러지면 게임이 0으로 끊는다 — 연출도 같이 끝낸다) */
  tauntTicks: number;
}

export interface RoleVfx {
  readonly role: PartyRole;
  /** 공격 한 번(slot = 파티 칸 1..3) */
  onAttack(ctx: PartyCtx, slot: number): void;
  /** 기술(사제 치유: arg = 대상 칸, 수호자 도발: arg = 도발 틱) */
  onSkill(ctx: PartyCtx, slot: number, arg: number): void;
  update(ctx: PartyCtx, dt: number, slot: number): void;
  clear(): void;
}

/** 보스 몸 표면에서 (fx,fz) 쪽 한 점(타격 자리) */
function surface(ctx: PartyCtx, fx: number, fz: number, out: { x: number; y: number; z: number }, jitter: number): void {
  const dx = fx - ctx.bossX, dz = fz - ctx.bossZ, d = Math.hypot(dx, dz) || 1;
  const r = ctx.bossR * 0.75;
  const a = ctx.add.range(-jitter, jitter);
  const c = Math.cos(a), s = Math.sin(a);
  const ux = dx / d * c - dz / d * s, uz = dz / d * c + dx / d * s;
  out.x = ctx.bossX + ux * r; out.z = ctx.bossZ + uz * r;
  out.y = ctx.bossMidY + ctx.add.range(-0.5, 0.6);
}
const P0 = { x: 0, y: 0, z: 0 };

/** 근접 타격 불똥(수호자·검사 공용) */
function meleeSparks(ctx: PartyCtx, slot: number, col: number, n: number): void {
  const P = ctx.add, fx = ctx.slotX[slot], fz = ctx.slotZ[slot];
  surface(ctx, fx, fz, P0, 0.25);
  const dx = fx - P0.x, dz = fz - P0.z, d = Math.hypot(dx, dz) || 1;
  P.emit(AddKind.Flare, Orient.Camera, P0.x, P0.y, P0.z, 0, 0, 0, 0.14, 0.7, 1.5, col, 2.2, 1);
  for (let c = 0; c < P.count(n, 3); c++) {
    const s = P.range(5, 11), a = P.range(-1.1, 1.1);
    const ux = dx / d * Math.cos(a) - dz / d * Math.sin(a), uz = dz / d * Math.cos(a) + dx / d * Math.sin(a);
    P.emit(AddKind.Streak, Orient.Velocity, P0.x, P0.y, P0.z, ux * s, P.range(1, 6), uz * s, P.range(0.16, 0.3), 0.07, 0.03, c % 3 === 0 ? 0xffffff : col, 2.3, 1, -16, 2, 0.045);
  }
}

class TankVfx implements RoleVfx {
  readonly role = 'tank' as const;
  private tauntLeft = 0;
  private tauntTotal = 1;
  private ringId = -1;
  private shoutT = -1;

  onAttack(ctx: PartyCtx, slot: number): void { meleeSparks(ctx, slot, 0xbfd8ff, 7); }

  onSkill(ctx: PartyCtx, slot: number, ticks: number): void {
    const P = ctx.add, G = ctx.glow, x = ctx.slotX[slot], z = ctx.slotZ[slot], col = ROLE_COLOR.tank;
    this.tauntTotal = this.tauntLeft = Math.max(0.5, ticks / 60);
    this.shoutT = 0;
    // 외침: 바닥 고리 두 겹(빠른 흰 고리 + 느린 파랑 고리) + 가슴 높이 공중 고리 + 퍼지는 빛 불티
    G.add(GlowKind.Shock, x, z, 0, 0.6, 7.5, 0.6, 7.5, 0.55, 0xdde9ff, 1.6, 0.9);
    G.add(GlowKind.Shock, x, z, 0, 0.6, 5.0, 0.6, 5.0, 0.8, col, 1.8, 0.85);
    P.emit(AddKind.Ring, Orient.Camera, x, 1.4, z, 0, 0, 0, 0.45, 0.8, 5.5, col, 1.8, 0.9);
    for (let c = 0; c < P.count(16, 6); c++) {
      const a = (c / 16) * Math.PI * 2 + P.range(-0.1, 0.1), s = P.range(6, 9);
      P.emit(AddKind.Streak, Orient.Velocity, x, 1.1, z, Math.sin(a) * s, P.range(0, 1.5), Math.cos(a) * s, 0.32, 0.08, 0.03, col, 1.8, 0.8, 0, 3, 0.05);
    }
    // 보스 쪽으로 뻗는 도발 줄(가렌 → 비룡)
    const dx = ctx.bossX - x, dz = ctx.bossZ - z, d = Math.hypot(dx, dz) || 1;
    for (let c = 0; c < P.count(8, 3); c++) {
      const s = P.range(14, 22);
      P.emit(AddKind.Streak, Orient.Velocity, x, 1.3 + P.range(-0.2, 0.3), z, dx / d * s, P.range(0.5, 2.5), dz / d * s, Math.min(0.45, d / s), 0.1, 0.05, col, 2.0, 0.9, 0, 0, 0.06);
    }
    if (this.ringId >= 0) G.end(this.ringId, 0.05);
    this.ringId = G.add(GlowKind.Taunt, x, z, 0, 1.25, 1.35, 1.25, 1.35, this.tauntLeft + 0.3, col, 1.6, 0.85);
  }

  update(ctx: PartyCtx, dt: number, slot: number): void {
    if (this.shoutT >= 0) { this.shoutT += dt; if (this.shoutT > 0.6) this.shoutT = -1; }
    if (this.tauntLeft <= 0) return;
    this.tauntLeft = Math.max(0, this.tauntLeft - dt);
    // 게임 도발이 일찍 끝났으면(수호자 쓰러짐) 0.2초 안에 끈다
    if (ctx.tauntTicks <= 0 && this.tauntTotal - this.tauntLeft > 0.15 && this.tauntLeft > 0.2) {
      this.tauntLeft = 0.2;
      if (this.ringId >= 0) { ctx.glow.end(this.ringId, 0.2); this.ringId = -1; }
    }
    const x = ctx.slotX[slot], z = ctx.slotZ[slot], M = ctx.marks, col = ROLE_COLOR.tank;
    if (this.ringId >= 0 && !ctx.glow.move(this.ringId, x, z)) this.ringId = -1;
    const fadeIn = Math.min(1, (this.tauntTotal - this.tauntLeft) / 0.15), fadeOut = Math.min(1, this.tauntLeft / 0.4);
    const k = fadeIn * fadeOut;
    // 방패 빛: 몸 앞(보스 쪽) 가슴 높이에서 맥동. 외침 순간 크게
    const dx = ctx.bossX - x, dz = ctx.bossZ - z, d = Math.hypot(dx, dz) || 1;
    const burst = this.shoutT >= 0 ? 1 - this.shoutT / 0.6 : 0;
    const pulse = 0.85 + 0.15 * Math.sin(ctx.time * 9);
    M.set(PARTY_MARKER.tankShield, AddKind.Flare, Orient.Camera, x + dx / d * 0.55, 1.15, z + dz / d * 0.55, (0.8 + 1.0 * burst) * pulse, 1, col, 1.2, 0.6 * k);
    // 보스 머리 위 위협 글리프(도발 중 비룡은 가렌만 노린다): 위아래로 살짝 떠다닌다
    const bob = Math.sin(ctx.time * 4) * 0.18;
    M.set(PARTY_MARKER.threatGlyph, AddKind.Glyph, Orient.Camera, ctx.bossX, ctx.bossTopY + 1.2 + bob, ctx.bossZ, 1.5, 1, col, 1.9, 0.95 * k);
    // 발밑에서 오르는 푸른 불티(약하게)
    if (ctx.add.rnd() < dt * 10 * ctx.add.scale) {
      const a = ctx.add.range(0, Math.PI * 2);
      ctx.add.emit(AddKind.Ember, Orient.Camera, x + Math.sin(a) * 1.2, 0.2, z + Math.cos(a) * 1.2, 0, ctx.add.range(1.5, 3), 0, 0.7, 0.14, 0.04, col, 1.7, 0.8 * k, 0.5, 0.5);
    }
  }

  clear(): void { this.tauntLeft = 0; this.ringId = -1; this.shoutT = -1; }
}

class ArcherVfx implements RoleVfx {
  readonly role = 'archer' as const;
  onAttack(ctx: PartyCtx, slot: number): void {
    const x = ctx.slotX[slot], z = ctx.slotZ[slot];
    surface(ctx, x, z, P0, 0.35);
    const d = Math.hypot(P0.x - x, P0.z - z);
    // 활 앞 작은 섬광(시위를 놓는 순간)
    const ux = (P0.x - x) / (d || 1), uz = (P0.z - z) / (d || 1);
    ctx.add.emit(AddKind.Flare, Orient.Camera, x + ux * 0.6, 1.4, z + uz * 0.6, 0, 0, 0, 0.12, 0.5, 0.9, ROLE_COLOR.archer, 1.8, 0.8);
    ctx.proj.fire(ProjKind.Arrow, x + ux * 0.6, 1.4, z + uz * 0.6, P0.x, P0.y, P0.z, Math.max(0.12, d / 42), Math.min(1.2, d * 0.05), ROLE_COLOR.archer);
  }
  onSkill(): void { /* 궁수 기술은 아직 없다 */ }
  update(): void { /* 상태 없음 */ }
  clear(): void { /* 상태 없음 */ }
}

class PriestVfx implements RoleVfx {
  readonly role = 'priest' as const;
  /** 빛기둥 칸별 남은 시간·대상 칸 */
  private readonly pLeft = new Float32Array(PARTY_MARKER.pillarN - PARTY_MARKER.pillar0 + 1);
  private readonly pSlot = new Int8Array(PARTY_MARKER.pillarN - PARTY_MARKER.pillar0 + 1);
  private castLeft = 0;
  private next = 0;

  onAttack(ctx: PartyCtx, slot: number): void {
    const x = ctx.slotX[slot], z = ctx.slotZ[slot];
    surface(ctx, x, z, P0, 0.4);
    const d = Math.hypot(P0.x - x, P0.z - z);
    ctx.proj.fire(ProjKind.LightOrb, x, 2.0, z, P0.x, P0.y, P0.z, Math.max(0.2, d / 18), 1.2, 0xfff0b8);
  }

  onSkill(ctx: PartyCtx, slot: number, target: number): void {
    const P = ctx.add, G = ctx.glow, col = ROLE_COLOR.priest;
    const px = ctx.slotX[slot], pz = ctx.slotZ[slot];
    const t = Math.max(0, Math.min(ctx.slotX.length - 1, target | 0));
    const tx = ctx.slotX[t], tz = ctx.slotZ[t];
    this.castLeft = 0.7;
    // 사제 발밑 시전 원(룬) + 대상 발밑 치유 원
    G.add(GlowKind.Heal, px, pz, 0, 1.6, 2.2, 1.6, 2.2, 0.9, col, 1.5, 0.85);
    G.add(GlowKind.Heal, tx, tz, 0, 1.0, 1.6, 1.0, 1.6, 1.3, col, 1.7, 0.95);
    G.add(GlowKind.Shock, tx, tz, 0, 0.4, 2.6, 0.4, 2.6, 0.6, 0xfff6d8, 1.5, 0.7);
    // 빛기둥(대상을 따라간다)
    const k = this.next; this.next = (this.next + 1) % this.pLeft.length;
    this.pLeft[k] = 1.15; this.pSlot[k] = t;
    // 금빛 입자: 대상 둘레에서 위로
    for (let c = 0; c < P.count(18, 6); c++) {
      const a = P.range(0, Math.PI * 2), r = P.range(0.2, 1.1);
      P.emit(AddKind.Ember, Orient.Camera, tx + Math.sin(a) * r, P.range(0.1, 1.2), tz + Math.cos(a) * r, Math.cos(a) * 0.4, P.range(1.8, 4), -Math.sin(a) * 0.4,
        P.range(0.6, 1.1), P.range(0.12, 0.2), 0.03, c % 3 ? col : 0xffffff, 1.9, 0.9, 0.6, 1);
    }
    // 사제 → 대상 금빛 줄기(대상이 사제 자신이 아니면)
    const dx = tx - px, dz = tz - pz, d = Math.hypot(dx, dz);
    if (d > 1) {
      for (let c = 0; c < P.count(10, 4); c++) {
        const s = P.range(12, 18), f = P.range(0, 0.25);
        P.emit(AddKind.Streak, Orient.Velocity, px + dx * f, 1.9 - f, pz + dz * f, dx / d * s, -0.5, dz / d * s, Math.min(0.5, d * (1 - f) / s), 0.09, 0.05, col, 2.0, 0.85, 0, 0, 0.05);
      }
    }
  }

  update(ctx: PartyCtx, dt: number, slot: number): void {
    const M = ctx.marks, col = ROLE_COLOR.priest;
    for (let k = 0; k < this.pLeft.length; k++) {
      if (this.pLeft[k] <= 0) continue;
      this.pLeft[k] = Math.max(0, this.pLeft[k] - dt);
      const age = 1.15 - this.pLeft[k];
      const grow = Math.min(1, age / 0.15), fade = Math.min(1, this.pLeft[k] / 0.45);
      const s = this.pSlot[k], x = ctx.slotX[s], z = ctx.slotZ[s];
      // 세운 판: 바닥(0)부터 높이 6.5m, 폭 1.4m(들어올 때 좁았다가 넓어진다)
      const wdt = 1.4 * (0.35 + 0.65 * grow);
      M.set(PARTY_MARKER.pillar0 + k, AddKind.Pillar, Orient.Upright, x, 3.25, z, wdt, 6.5 / wdt, col, 1.7, 0.95 * fade * grow, 0, k * 0.37);
    }
    if (this.castLeft > 0) {
      this.castLeft = Math.max(0, this.castLeft - dt);
      const x = ctx.slotX[slot], z = ctx.slotZ[slot];
      // 지팡이 끝 빛
      M.set(PARTY_MARKER.castGlow, AddKind.Flare, Orient.Camera, x, 2.1, z, 0.9 + 0.4 * Math.sin(ctx.time * 20), 1, col, 2.0, Math.min(1, this.castLeft / 0.3));
    }
  }

  clear(): void { this.pLeft.fill(0); this.castLeft = 0; }
}

class MageVfx implements RoleVfx {
  readonly role = 'mage' as const;
  onAttack(ctx: PartyCtx, slot: number): void {
    const x = ctx.slotX[slot], z = ctx.slotZ[slot];
    surface(ctx, x, z, P0, 0.4);
    const d = Math.hypot(P0.x - x, P0.z - z);
    ctx.add.emit(AddKind.Ring, Orient.Camera, x, 1.8, z, 0, 0, 0, 0.25, 0.4, 1.4, ROLE_COLOR.mage, 1.8, 0.8);
    ctx.proj.fire(ProjKind.ArcaneBolt, x, 1.8, z, P0.x, P0.y, P0.z, Math.max(0.18, d / 24), 0.8, ROLE_COLOR.mage);
  }
  onSkill(ctx: PartyCtx, slot: number): void {
    ctx.glow.add(GlowKind.Heal, ctx.slotX[slot], ctx.slotZ[slot], 0, 1.2, 1.8, 1.2, 1.8, 0.8, ROLE_COLOR.mage, 1.5, 0.8);
  }
  update(): void { /* 상태 없음 */ }
  clear(): void { /* 상태 없음 */ }
}

class WarriorVfx implements RoleVfx {
  readonly role = 'warrior' as const;
  onAttack(ctx: PartyCtx, slot: number): void {
    const P = ctx.add, fx = ctx.slotX[slot], fz = ctx.slotZ[slot];
    surface(ctx, fx, fz, P0, 0.2);
    // 엇갈린 참격 줄 두 개(짧고 굵은 흰 심 + 주황 테)
    for (let c = 0; c < 2; c++) {
      const a = c === 0 ? 0.7 : -0.7, s = 16;
      const dx = fx - P0.x, dz = fz - P0.z, d = Math.hypot(dx, dz) || 1;
      const tx = -dz / d, tz = dx / d;
      P.emit(AddKind.Streak, Orient.Velocity, P0.x - tx * 0.9, P0.y + a, P0.z - tz * 0.9, tx * s, -a * s * 0.9, tz * s, 0.14, 0.22, 0.12, c ? 0xffffff : ROLE_COLOR.warrior, 2.4, 1, 0, 6, 0.09);
    }
    meleeSparks(ctx, slot, ROLE_COLOR.warrior, 6);
  }
  onSkill(ctx: PartyCtx, slot: number): void {
    ctx.glow.add(GlowKind.Shock, ctx.slotX[slot], ctx.slotZ[slot], 0, 0.5, 3.5, 0.5, 3.5, 0.5, ROLE_COLOR.warrior, 1.6, 0.85);
  }
  update(): void { /* 상태 없음 */ }
  clear(): void { /* 상태 없음 */ }
}

/** 직업 → 모듈(새 직업 AI는 여기 한 줄) */
export function createRoleVfx(role: PartyRole): RoleVfx {
  switch (role) {
    case 'tank': return new TankVfx();
    case 'archer': return new ArcherVfx();
    case 'priest': return new PriestVfx();
    case 'mage': return new MageVfx();
    default: return new WarriorVfx();
  }
}

/** 모르는 역할 문자열(새 직업 이름 등)도 가장 가까운 모듈로 */
export function roleOf(role: string): PartyRole {
  if ((PARTY_ROLES as readonly string[]).includes(role)) return role as PartyRole;
  if (/guard|tank|수호/.test(role)) return 'tank';
  if (/arch|bow|궁/.test(role)) return 'archer';
  if (/priest|heal|cleric|사제/.test(role)) return 'priest';
  if (/mage|wizard|마법/.test(role)) return 'mage';
  return 'warrior';
}

/** 공통: 쓰러짐(흙먼지) · 일어남(금빛 고리) · 피하기(발 먼지) */
export function commonDown(ctx: PartyCtx, alpha: RaidParticles, x: number, z: number): void {
  for (let c = 0; c < alpha.count(6, 3); c++) {
    const a = alpha.range(0, Math.PI * 2), s = alpha.range(1, 2.4);
    alpha.emit(0, Orient.Camera, x, 0.3, z, Math.sin(a) * s, alpha.range(0.3, 1), Math.cos(a) * s, alpha.range(0.6, 0.9), 0.5, 1.4, 0x8a7a68, 1, 0.55, 0.2, 2.2);
  }
  void ctx;
}
export function commonRevive(ctx: PartyCtx, x: number, z: number): void {
  ctx.glow.add(GlowKind.Shock, x, z, 0, 0.3, 2.2, 0.3, 2.2, 0.6, 0xffe6a0, 1.6, 0.9);
  ctx.glow.add(GlowKind.Heal, x, z, 0, 1.0, 1.3, 1.0, 1.3, 0.9, 0xffe6a0, 1.4, 0.8);
  for (let c = 0; c < ctx.add.count(10, 4); c++) {
    const a = ctx.add.range(0, Math.PI * 2);
    ctx.add.emit(AddKind.Ember, Orient.Camera, x + Math.sin(a) * 0.6, 0.3, z + Math.cos(a) * 0.6, 0, ctx.add.range(2, 4), 0, 0.8, 0.16, 0.03, 0xffe6a0, 1.8, 0.9, 0.5, 1);
  }
}
export function commonDodge(alpha: RaidParticles, x: number, z: number): void {
  for (let c = 0; c < alpha.count(3, 2); c++) {
    alpha.emit(0, Orient.Camera, x + alpha.range(-0.3, 0.3), 0.2, z + alpha.range(-0.3, 0.3), alpha.range(-1, 1), alpha.range(0.2, 0.6), alpha.range(-1, 1), 0.5, 0.35, 0.9, 0x8a7a68, 1, 0.45, 0, 2.5);
  }
}
